import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import classNames from "classnames";
import Button from "../../atomic/Button";
import Divider from "../../atomic/Divider";
import type { NavItemComponentProps } from "./types";
import { useNavbarContext } from "./NavbarContext";
import { filterVisibleItems, getInitials } from "./utils";

const profileMenuItemClassName =
  "block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50";

export function ProfileMenu({ item }: NavItemComponentProps) {
  const { user, navigateFromSource } = useNavbarContext();
  const menuItems = filterVisibleItems(item.items ?? []);
  const showInitials = item.avatar?.showInitials !== false;
  const showName = item.userInfo?.showName !== false;
  const showEmail = item.userInfo?.showEmail !== false;
  const initials = getInitials(user?.name);
  const showUserInfo = (showName || showEmail) && (user?.name || user?.email);

  if (menuItems.length === 0) return null;

  return (
    <Menu as="div" className="relative ml-3">
      <Menu.Button
        className="flex rounded-full p-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        style={{ backgroundColor: "var(--navbar-text-active)" }}
        aria-label="Open profile menu"
      >
        <span className="sr-only">Open user menu</span>
        {showInitials && initials ? (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium text-white"
            style={{
              backgroundColor: "var(--navbar-text-active)",
              border: "1px solid var(--navbar-border)"
            }}
          >
            {initials}
          </div>
        ) : (
          <div
            className="h-8 w-8 rounded-full"
            style={{ backgroundColor: "var(--navbar-border)" }}
          />
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 min-w-[220px] origin-top-right rounded-md bg-white py-1 text-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {showUserInfo && (
            <div className="mb-2 flex items-start px-4 py-2">
              {showInitials && initials ? (
                <div
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: "var(--navbar-text-active)" }}
                >
                  {initials}
                </div>
              ) : null}
              <div
                className={classNames(
                  "min-w-0",
                  showInitials && initials && "ml-2"
                )}
              >
                {showName && user?.name && (
                  <div
                    className="mb-1 text-sm font-medium text-gray-700"
                    title={user.name}
                  >
                    {user.name}
                  </div>
                )}
                {showEmail && user?.email && (
                  <div
                    className="max-w-[150px] truncate text-xs text-gray-500"
                    title={user.email}
                  >
                    {user.email}
                  </div>
                )}
              </div>
            </div>
          )}

          {showUserInfo && <Divider className="mb-2" />}

          {menuItems.map((menuItem) => (
            <Menu.Item key={menuItem.id}>
              {({ active }) => (
                <Button
                  onClick={() => navigateFromSource(menuItem.source)}
                  className={classNames(
                    profileMenuItemClassName,
                    "rounded-none border-0 !bg-transparent font-normal shadow-none hover:!bg-indigo-50",
                    active && "!bg-gray-50"
                  )}
                  pxFour={false}
                >
                  {menuItem.label}
                </Button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
